import { DatabaseTransformer, DatabaseRow } from './DatabaseTransformer';
import { TransformationUtils } from '../TransformationUtils';
import { TransformationContext } from '../types';
import { Service } from '../../../types/entities';

/**
 * Service database row structure
 */
export interface ServiceRow extends DatabaseRow {
  name: string;
  description?: string | null;
  base_price: number;
  duration: number;
  category?: string | null;
  add_ons?: string[] | null;
  is_active?: boolean;
  service_type_id?: number | null;
  requires_bedrooms?: boolean;
  requires_bathrooms?: boolean;
  requires_square_footage?: boolean;
  requires_frequency?: boolean;
}

/**
 * Service transformer - converts database ServiceRow to frontend Service
 */
export class ServiceTransformer extends DatabaseTransformer<ServiceRow, Service> {
  protected performEntityTransformation(
    baseOutput: Record<string, unknown>,
    input: ServiceRow,
    _context?: TransformationContext
  ): Service {
    const base = baseOutput as Partial<Service>;
    return {
      id: base.id || input.id,
      createdAt: base.createdAt || new Date().toISOString(),
      updatedAt: base.updatedAt || new Date().toISOString(),
      name: TransformationUtils.toString(input.name),
      description: TransformationUtils.toString(input.description || ''),
      basePrice: TransformationUtils.toNumber(input.base_price, 0),
      duration: TransformationUtils.toNumber(input.duration, 60),
      category: this.normalizeCategory(input.category),
      addOns: TransformationUtils.toStringArray(input.add_ons),
      isActive: TransformationUtils.toBoolean(input.is_active, true),
      serviceTypeId: input.service_type_id ?? undefined,
      requiresBedrooms: TransformationUtils.toBoolean(input.requires_bedrooms, false),
      requiresBathrooms: TransformationUtils.toBoolean(input.requires_bathrooms, false),
      requiresSquareFootage: TransformationUtils.toBoolean(input.requires_square_footage, false),
      requiresFrequency: TransformationUtils.toBoolean(input.requires_frequency, true)
    };
  }

  /**
   * Normalize category to valid enum value
   */
  private normalizeCategory(category: string | null | undefined): 'residential' | 'commercial' | 'deep-cleaning' | 'specialized' {
    type ValidCategory = 'residential' | 'commercial' | 'deep-cleaning' | 'specialized';
    const validCategories: readonly ValidCategory[] = ['residential', 'commercial', 'deep-cleaning', 'specialized'] as const;
    const normalized = (category || 'residential').toLowerCase();
    
    return (validCategories as readonly string[]).includes(normalized) 
      ? normalized as ValidCategory 
      : 'residential';
  }

  /**
   * Static factory method
   */
  static create(): ServiceTransformer {
    return new ServiceTransformer();
  }

  /**
   * Transform single service
   */
  static transformOne(serviceRow: ServiceRow, context?: TransformationContext): Service {
    return ServiceTransformer.create().transform(serviceRow, context).data!;
  }

  /**
   * Transform array of services
   */
  static transformMany(serviceRows: ServiceRow[], context?: TransformationContext): Service[] {
    return ServiceTransformer.create().transformArray(serviceRows, context).data!;
  }
}